import React, { useState, useEffect, useMemo } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { Truck, Package, AlertTriangle, FileText, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import { useAccount } from "../context/AccountContext";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import ClientQuotes from "./ClientQuotes";
import UserTransportOrders from "./UserTransportOrders";
import UserProofOfDelivery from "./UserProofOfDelivery";
import UserIssues from "./UserIssues";
import PartialDispatchMap from "../Tracking/PartialDispatchMap";

import "./ClientDashboard.css";

const mapContainerStyle = { width: "100%", height: "160px", borderRadius: "12px" };
const mapOptions = { disableDefaultUI: true, zoomControl: false, gestureHandling: "none" };

export default function ClientDashboard() {
  const [activePage, setActivePage] = useState(null);
  const { currentAccount } = useAccount();
  const { userId: currentUserId } = useGetCurrentUserId();
const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalOrders: 0,
    inProcess: 0,
    liveTracking: 0,
    pendingQuotes: 0,
    issues: 0,
    documents: 0,
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [latestLiveOrder, setLatestLiveOrder] = useState(null);
  const [recentIssues, setRecentIssues] = useState([]);
  const [recentPods, setRecentPods] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);

  const effectiveUid = currentAccount?.uid || currentUserId;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  useEffect(() => {
    if (!effectiveUid) return;

    const db = getDatabase();

    // Orders + Live Tracking
    const ordersRef = ref(db, "transport_orders");
    onValue(ordersRef, (snapshot) => {
      const data = snapshot.val() || {};
      let total = 0, inProcess = 0, liveCount = 0;
      const recent = [];
      let latestLive = null;

      Object.values(data).forEach((transporterOrders) => {
        Object.values(transporterOrders || {}).forEach((order) => {
          const isMyOrder =
            order.clientUid === effectiveUid ||
            order.clientId === effectiveUid ||
            order.companyName === currentAccount?.company_name;

          if (!isMyOrder) return;

          total++;

          const isInProcess =
            order.status?.trim().toLowerCase() === "in-process";

          if (isInProcess) {
            inProcess++;
            recent.push(order); // Store all In-Process orders
          }

          const hasLive = order.dispatch?.items?.some(
            (item) => item?.location?.sharing === true
          );

          if (hasLive) {
            liveCount++;

            if (
              !latestLive ||
              (order.orderCreatedAt || order.createdAt || 0) >
              (latestLive.orderCreatedAt || latestLive.createdAt || 0)
            ) {
              latestLive = order;
            }
          }
        });
      });

      recent.sort(
        (a, b) =>
          (b.orderCreatedAt || b.createdAt || 0) -
          (a.orderCreatedAt || a.createdAt || 0)
      );

      setStats((prev) => ({
        ...prev,
        totalOrders: total,
        inProcess,
        liveTracking: liveCount,
      }));

      setRecentOrders(recent.slice(0, 2)); // Latest 2 In-Process orders
      setLatestLiveOrder(latestLive);

    });

    // === ISSUES ===
    const issuesRef = ref(db, "transport_issues");
    onValue(issuesRef, (snapshot) => {
      const data = snapshot.val() || {};
      let count = 0;
      const recent = [];

      Object.values(data).forEach(transporterIssues => {
        Object.values(transporterIssues || {}).forEach(issue => {
          if (issue.clientId === effectiveUid) {
            count++;
            recent.push(issue);
          }
        });
      });

      recent.sort((a, b) => (b.reportedAt || 0) - (a.reportedAt || 0));
      setStats(prev => ({ ...prev, issues: count }));
      setRecentIssues(recent.slice(0, 2));
    });
   // === PODs & Invoices (Bills & POD) ===
const docsRef = ref(db, "transport_documents");
const ordersRefForDocs = ref(db, "transport_orders");

// We keep a set of orderIds that belong to this client
let myOrderIds = new Set();

const isMyDocument = (doc, orderId) => {
  if (!doc) return false;

  // 1. Direct match on the document itself
  if (
    doc.clientId === effectiveUid ||
    doc.clientUid === effectiveUid ||
    doc.userId === effectiveUid
  ) {
    return true;
  }

  // 2. Fallback – order belongs to this client
  if (orderId && myOrderIds.has(orderId)) {
    return true;
  }

  return false;
};

const processAllDocuments = (docsData) => {
  const allDocs = [];

  Object.entries(docsData || {}).forEach(([transporterId, transporterDocs]) => {
    if (!transporterDocs || typeof transporterDocs !== "object") return;

    Object.entries(transporterDocs).forEach(([orderId, orderDocs]) => {
      // skip accidental keys
      if (
        orderId === "POD" ||
        orderId === "invoices" ||
        orderId === "payments"
      )
        return;
      if (!orderDocs || typeof orderDocs !== "object") return;

      // ---------- PODs ----------
      if (orderDocs.POD && typeof orderDocs.POD === "object") {
        Object.entries(orderDocs.POD).forEach(([podId, pod]) => {
          if (isMyDocument(pod, orderId)) {
            allDocs.push({
              ...pod,
              id: podId,
              transporterId,
              orderId,
              type: "POD",
              fileName: pod.fileName || "POD Document",
              uploadedAt: pod.uploadedAt || 0,
            });
          }
        });
      }

      // ---------- Invoices ----------
      if (orderDocs.invoices && typeof orderDocs.invoices === "object") {
        Object.entries(orderDocs.invoices).forEach(([invoiceId, invoice]) => {
          if (isMyDocument(invoice, orderId)) {
            allDocs.push({
              ...invoice,
              id: invoiceId,
              transporterId,
              orderId,
              type: "Invoice",
              fileName:
                invoice.fileName || invoice.billNumber || "Invoice",
              uploadedAt: invoice.uploadedAt || 0,
            });
          }
        });
      }

      // ---------- Payments ----------
      if (orderDocs.payments && typeof orderDocs.payments === "object") {
        Object.entries(orderDocs.payments).forEach(([payId, pay]) => {
          if (isMyDocument(pay, orderId)) {
            allDocs.push({
              ...pay,
              id: payId,
              transporterId,
              orderId,
              type: "Payment",
              fileName:
                pay.fileName ||
                (pay.utrNumber ? `UTR ${pay.utrNumber}` : "Payment"),
              uploadedAt: pay.uploadedAt || 0,
            });
          }
        });
      }
    });
  });

  // Newest first
  allDocs.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));

  setStats((prev) => ({
    ...prev,
    documents: allDocs.length,
  }));

  setRecentPods(allDocs.slice(0, 2));
};

// 1. Keep the list of my orderIds up-to-date
onValue(ordersRefForDocs, (snapshot) => {
  const data = snapshot.val() || {};
  const ids = new Set();

  Object.values(data).forEach((transporterOrders) => {
    Object.values(transporterOrders || {}).forEach((order) => {
      const isMine =
        order.clientUid === effectiveUid ||
        order.clientId === effectiveUid ||
        order.companyName === currentAccount?.company_name;

      if (isMine && order.orderId) {
        ids.add(order.orderId);
      }
    });
  });

  myOrderIds = ids;
});

// 2. Listen to documents
onValue(docsRef, (snapshot) => {
  processAllDocuments(snapshot.val() || {});
});
    // === QUOTES / REQUESTS ===
    const requestsRef = ref(db, "transport_requests");

    onValue(requestsRef, (snapshot) => {
      const data = snapshot.val() || {};
      let pending = 0;
      const recent = [];

      Object.values(data).forEach((transporterReqs) => {
        Object.values(transporterReqs || {}).forEach((req) => {
          const isMyRequest =
            req.clientId === effectiveUid || req.userId === effectiveUid;

          const isWaiting =
            req.status?.toLowerCase() === "waiting for response";

          if (isMyRequest && isWaiting) {
            pending++;
            recent.push(req);
          }
        });
      });

      recent.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setStats((prev) => ({
        ...prev,
        pendingQuotes: pending,
      }));

      setRecentQuotes(recent.slice(0, 2));
    });
  }, [effectiveUid, currentAccount?.company_name]);

  const SmallLiveMap = React.memo(({ order }) => {
    const liveItem = order?.dispatch?.items?.find(
      (item) => item?.location?.sharing === true
    );

    const location = liveItem?.location;

    const center = useMemo(
      () => ({
        lat: location?.lat || 0,
        lng: location?.lng || 0,
      }),
      [location]
    );
    const truckNumber =
      liveItem?.truckNumber ||
      "N/A";

    const driverName = liveItem?.driverName || "N/A";

    const truckIcon = {
      url: "https://maps.google.com/mapfiles/kml/shapes/truck.png",
      scaledSize: new window.google.maps.Size(26, 26),
    };


    if (!location || !isLoaded)
      return <div className="map-placeholder">Loading map...</div>;

    return (
      <div>
         

      <div className="mini-map-container">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={15}
          options={mapOptions}
        >
          {isLoaded && truckIcon && (
            <Marker
              position={{
                lat: location.lat,
                lng: location.lng,
              }}
              icon={truckIcon}
            />
          )}
        </GoogleMap>


        <div className="mini-map-label">
          {/* <div><strong>Order:</strong> #{order.orderId} • LIVE</div> */}
          <div><strong>Truck:</strong> {truckNumber}</div>
        </div>
      </div>
     <div>
  <strong>Order:</strong> #{order.orderId}
  <span className="live-status">
    <span className="live-dot"></span>
    LIVE
  </span>
</div>
      </div>
    );
  });
  

  const dashboardCards = [
    {
      id: "orders",
      title: "My Orders",
      icon: Package,
      count: stats.totalOrders,
      subtitle: `${stats.inProcess} In Process`,
      color: "#3b82f6",
      component: <UserTransportOrders />,
      preview: recentOrders.length > 0 ? recentOrders.map((o, i) => (
        <div key={i} className="preview-item">#{o.orderId} • {o.from?.slice(0, 12)} → {o.to?.slice(0, 12)}</div>
      )) : "No recent orders"
    },
    {
      id: "tracking",
      title: "Live Tracking",
      icon: MapPin,
      count: stats.liveTracking,
      subtitle: "Active Trucks",
      color: "#10b981",
      component: <PartialDispatchMap />,
      preview: latestLiveOrder ? (
        <SmallLiveMap order={latestLiveOrder} />
      ) : "No live tracking"
    },
    {
      id: "quotes",
      title: "Requests",
      icon: Truck,
      count: stats.pendingQuotes,
      subtitle: "Waiting Quotes",
      color: "#f59e0b",
      component: <ClientQuotes />,
      preview: recentQuotes.length > 0 ? recentQuotes.map((q, i) => (
        <div key={i} className="preview-item">{q.from?.slice(0, 15)} → {q.to?.slice(0, 15)}</div>
      )) : "No pending requests"
    },
    {
      id: "issues",
      title: "Issues",
      icon: AlertTriangle,
      count: stats.issues,
      subtitle: "Active Issues",
      color: "#ef4444",
      component: <UserIssues />,
      preview: recentIssues.length > 0 ? recentIssues.map((iss, i) => (
        <div key={i} className="preview-item">{iss.issue?.slice(0, 35)}...</div>
      )) : "No issues"
    },
    {
      id: "pod",
      title: "Bills & POD",
      icon: FileText,
      count: stats.documents,
      subtitle: "Documents",
      color: "#8b5cf6",
      component: <UserProofOfDelivery />,
    preview:
  recentPods.length > 0
    ? recentPods.map((p, i) => (
        <div key={i} className="preview-item">
          #{p.orderId} • {p.type} • {(p.fileName || "").slice(0, 20)}
        </div>
      ))
    : "No documents",
    },
  ];

  const activeCard = dashboardCards.find(c => c.id === activePage);

  return (
    <div className="tp-client-dashboard">
      <div className="tp-dashboard-header">
        <h1>Transport Dashboard</h1>
        <p>Welcome back, {currentAccount?.company_name || "Client"}</p>
      </div>

      {!activePage ? (
        <div className="tp-dashboard-grid">
          {dashboardCards.map((card) => {
            const Icon = card.icon;
            return (
           <div
  key={card.id}
  className="tp-dashboard-card"
  onClick={() => navigate(`/dashboard/transport/${card.id}`)}

>
                <div className="tp-card-header">
                  <div className="tp-card-icon-wrapper" style={{ backgroundColor: `${card.color}15` }}>
                    <Icon size={32} color={card.color} />
                  </div>
                  <div className="tp-card-count" style={{ color: card.color }}>{card.count}</div>
                </div>

                <h3 className="tp-card-title">{card.title}</h3>
                <p className="tp-card-subtitle">{card.subtitle}</p>

                <div className="tp-card-preview">
                  {card.preview}
                </div>

                <div className="tp-card-footer">
                  <span className="tp-view-link" style={{ color: card.color }}>View Details <ArrowRight size={16} className="tp-arrow" /></span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tp-full-page-view">
          <button className="tp-back-button" onClick={() => setActivePage(null)}>
            <ArrowLeft size={20} /> Back to Dashboard
          </button>
          {activeCard?.component}
        </div>
      )}
    </div>
  );
}